import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Badge } from "../Components/ui/badge";
import { Input } from "../Components/ui/input";
import { Textarea } from "../Components/ui/textarea";
import {
  Globe,
  Award,
  TrendingUp,
  Mail,
  Calendar,
  Monitor,
  Loader2,
  ArrowRight,
  CheckCircle,
  Star,
  Shield,
  Zap
} from "lucide-react";



const benefits = [
  {
    icon: Shield,
    color: "emerald"
  },
  {
    icon: Zap,
    color: "emerald"
  },
  {
    icon: Globe,
    color: "emerald"
  },
  {
    icon: TrendingUp,
    color: "emerald"
  }
];

export default function Distributors() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const copy = t("distributors");
  const [formData, setFormData] = useState({
    companyName: '',
    phone: '',
    email: '',
    country: '',
    experience: '',
    salesChannels: [],
    comments: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleApplyNow = () => {
    const element = document.getElementById('distributor-application');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnMore = () => {
    navigate('/about');
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleViewProductDetails = () => {
    navigate('/kylendar');
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = copy.application.validation.companyName;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = copy.application.validation.phone;
    }

    if (!formData.email.trim()) {
      newErrors.email = copy.application.validation.email;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = copy.application.validation.emailInvalid;
    }

    if (!formData.country.trim()) {
      newErrors.country = copy.application.validation.country;
    }

    if (!formData.experience) {
      newErrors.experience = copy.application.validation.experience;
    }

    if (formData.salesChannels.length === 0) {
      newErrors.salesChannels = copy.application.validation.salesChannels;
    }

    if (!formData.comments.trim()) {
      newErrors.comments = copy.application.validation.comments;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const requestData = {
          companyName: formData.companyName,
          phone: formData.phone,
          email: formData.email,
          country: formData.country,
          experience: formData.experience,
          salesChannels: formData.salesChannels,
          comments: formData.comments,
          submittedAt: new Date().toISOString()
        };

        const response = await fetch('/api/distributor/application', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });

        const result = await response.json();

        if (response.ok && result.success) {
          setIsSubmitted(true);
          // Reset form
          setFormData({
            companyName: '',
            phone: '',
            email: '',
            country: '',
            experience: '',
            salesChannels: [],
            comments: ''
          });
          setErrors({});
          // Redirect to thank you page after 1.5 seconds
          setTimeout(() => {
            navigate('/thank-you');
            // Ensure page scrolls to top after navigation
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
          }, 1500);
        } else {
          setErrors({ submit: copy.application.submission.genericFailure });
        }
      } catch {
        setErrors({ submit: copy.application.submission.networkFailure });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));

    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors(prev => ({
        ...prev,
        [e.target.name]: ''
      }));
    }
  };

  const handleSalesChannelChange = (channel) => {
    setFormData(prev => ({
      ...prev,
      salesChannels: prev.salesChannels.includes(channel)
        ? prev.salesChannels.filter(c => c !== channel)
        : [...prev.salesChannels, channel]
    }));

    // Clear error when user selects a channel
    if (errors.salesChannels) {
      setErrors(prev => ({
        ...prev,
        salesChannels: ''
      }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="distributors-hero" className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600 animate-bounce-in">
            {copy.hero.badge}
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 animate-slide-in-up">
            {t("interface.distributorHero")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 animate-fade-in">
            {copy.hero.body}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="gradient-green text-white hover:opacity-90 animate-pulse-glow hover:animate-wiggle" onClick={handleApplyNow}>
              {copy.hero.apply}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 animate-scale-in hover:animate-wiggle" onClick={handleLearnMore}>
              {copy.hero.learnMore}
            </Button>
          </div>
        </div>
      </section>



      {/* Product Showcase */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {t("interface.distributorShowcase")}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in">
              {copy.showcase.body}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <iframe
                className="w-full h-80 rounded-lg shadow-lg animate-float"
                src="https://www.youtube.com/embed/4_OxeHecsR8?autoplay=0&mute=0&loop=1&playlist=4_OxeHecsR8&controls=1&modestbranding=1&rel=0"
                title={copy.showcase.videoTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              >
              </iframe>
            </div>
            <div className="animate-slide-in-right">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {copy.showcase.productTitle}
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                {copy.showcase.productBody}
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  <Calendar className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[0]}</span>
                </div>
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <Zap className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[1]}</span>
                </div>
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  <Shield className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[2]}</span>
                </div>
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
                  <Monitor className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[3]}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="gradient-green text-white hover:opacity-90 animate-pulse-glow hover:animate-wiggle" onClick={handleViewProductDetails}>
                  {copy.showcase.button}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {t("interface.distributorBenefits")}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in">
              {copy.benefits.body}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover-lift text-center animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-${benefit.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse-glow hover:animate-wiggle`}>
                    <benefit.icon className={`w-8 h-8 text-${benefit.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {copy.benefits.items[index].title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {copy.benefits.items[index].description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements & Application */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Requirements */}
            <div className="animate-slide-in-left">
              <div className="p-6 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-6 h-6 text-gray-600 mr-2" />
                  {copy.requirements.title}
                </h3>
                <div className="space-y-3 text-gray-700">
                  {copy.requirements.items.map((requirement, index) => (
                    <div key={index} className="flex items-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-3 flex-shrink-0 animate-wiggle" />
                      <span>{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Market Opportunity */}
              <div className="mt-12 p-6 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600 mr-2" />
                  {copy.market.title}
                </h3>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-emerald-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.market.items[0]}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-emerald-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.market.items[1]}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-emerald-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.market.items[2]}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-emerald-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.market.items[3]}</span>
                  </div>
                </div>
              </div>

              {/* Support Services */}
              <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-blue-600 mr-2" />
                  {copy.support.title}
                </h3>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.support.items[0]}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.support.items[1]}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.support.items[2]}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                    <span>{copy.support.items[3]}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Form */}
            <Card id="distributor-application" className="border-0 shadow-lg animate-slide-in-right">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {copy.application.title}
                </CardTitle>
                <p className="text-gray-600">
                  {copy.application.body}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.companyName} *
                    </label>
                    <Input
                      name="companyName"
                      type="text"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder={copy.application.placeholders.companyName}
                      className={errors.companyName ? 'border-red-500 focus:border-red-500' : ''}
                      onInvalid={(e) => e.target.setCustomValidity(copy.application.validation.companyNameInput)}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.phone} *
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={copy.application.placeholders.phone}
                      className={errors.phone ? 'border-red-500 focus:border-red-500' : ''}
                      onInvalid={(e) => e.target.setCustomValidity(copy.application.validation.phoneInput)}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.email} *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={copy.application.placeholders.email}
                      className={errors.email ? 'border-red-500 focus:border-red-500' : ''}
                      onInvalid={(e) => e.target.setCustomValidity(copy.application.validation.emailInvalid)}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.country} *
                    </label>
                    <Input
                      name="country"
                      type="text"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder={copy.application.placeholders.country}
                      className={errors.country ? 'border-red-500 focus:border-red-500' : ''}
                      onInvalid={(e) => e.target.setCustomValidity(copy.application.validation.countryInput)}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                    {errors.country && (
                      <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.experience} *
                    </label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.experience ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">{copy.application.placeholders.experience}</option>
                      <option value="0-1">{copy.application.experienceOptions.lessThanOne}</option>
                      <option value="1-2">{copy.application.experienceOptions.oneToTwo}</option>
                      <option value="2-5">{copy.application.experienceOptions.twoToFive}</option>
                      <option value="5-10">{copy.application.experienceOptions.fiveToTen}</option>
                      <option value="10+">{copy.application.experienceOptions.tenPlus}</option>
                    </select>
                    {errors.experience && (
                      <p className="text-red-500 text-sm mt-1">{errors.experience}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.salesChannels} *
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={formData.salesChannels.includes('retail')}
                          onChange={() => handleSalesChannelChange('retail')}
                        />
                        <span className="text-sm">{copy.application.channels.retail}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={formData.salesChannels.includes('online')}
                          onChange={() => handleSalesChannelChange('online')}
                        />
                        <span className="text-sm">{copy.application.channels.online}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={formData.salesChannels.includes('b2b')}
                          onChange={() => handleSalesChannelChange('b2b')}
                        />
                        <span className="text-sm">{copy.application.channels.b2b}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={formData.salesChannels.includes('corporate')}
                          onChange={() => handleSalesChannelChange('corporate')}
                        />
                        <span className="text-sm">{copy.application.channels.corporate}</span>
                      </label>
                    </div>
                    {errors.salesChannels && (
                      <p className="text-red-500 text-sm mt-1">{errors.salesChannels}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {copy.application.fields.comments} *
                    </label>
                    <Textarea
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      rows={4}
                      className={errors.comments ? 'border-red-500 focus:border-red-500' : ''}
                      placeholder={copy.application.placeholders.comments}
                      onInvalid={(e) => e.target.setCustomValidity(copy.application.validation.commentsInput)}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                    {errors.comments && (
                      <p className="text-red-500 text-sm mt-1">{errors.comments}</p>
                    )}
                  </div>

                  {/* Success Message */}
                  {isSubmitted && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <div>
                          <h3 className="text-sm font-medium text-green-800">{copy.application.submission.successTitle}</h3>
                          <p className="text-sm text-green-600 mt-1">{copy.application.submission.successBody}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {errors.submit && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <div className="w-5 h-5 text-red-500 mr-2">⚠️</div>
                        <div>
                          <h3 className="text-sm font-medium text-red-800">{copy.application.submission.failed}</h3>
                          <p className="text-sm text-red-600 mt-1">{errors.submit}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full gradient-green text-white hover:opacity-90 animate-pulse-glow hover:animate-wiggle"
                    disabled={isSubmitting || isSubmitted}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {copy.application.submission.submitting}
                      </>
                    ) : isSubmitted ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        {copy.application.submission.submitted}
                      </>
                    ) : (
                      <>
                        {copy.application.submission.submit}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Stories & Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {t("interface.distributorStories")}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in">
              {copy.stories.body}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover-lift text-center animate-scale-in" style={{ animationDelay: '0.1s' }}>
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Star className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{copy.stories.items[0].name}</h3>
                <p className="text-gray-600 mb-4 italic">
                  “{copy.stories.items[0].quote}”
                </p>
                <div className="text-sm text-emerald-600 font-semibold">
                  {copy.stories.items[0].byline}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center animate-scale-in" style={{ animationDelay: '0.2s' }}>
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{copy.stories.items[1].name}</h3>
                <p className="text-gray-600 mb-4 italic">
                  “{copy.stories.items[1].quote}”
                </p>
                <div className="text-sm text-blue-600 font-semibold">
                  {copy.stories.items[1].byline}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover-lift text-center animate-scale-in" style={{ animationDelay: '0.3s' }}>
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{copy.stories.items[2].name}</h3>
                <p className="text-gray-600 mb-4 italic">
                  “{copy.stories.items[2].quote}”
                </p>
                <div className="text-sm text-purple-600 font-semibold">
                  {copy.stories.items[2].byline}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {t("interface.distributorFaq")}
            </h2>
            <p className="text-lg text-gray-600 animate-fade-in">
              {copy.faq.body}
            </p>
          </div>

          <div className="space-y-6">
            {copy.faq.items.map((item, index) => (
            <Card key={item.question} className="border-0 shadow-lg animate-fade-in" style={{ animationDelay: `${(index + 1) * 0.1}s` }}>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                <p className="text-gray-600">{item.answer}</p>
              </CardContent>
            </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 animate-slide-in-up">
              {t("interface.distributorCta")}
            </h2>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto animate-fade-in">
              {copy.cta.body}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="text-center animate-scale-in" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse-glow hover:animate-wiggle">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{copy.cta.emailTitle}</h3>
              <p className="text-emerald-100 mb-4">business@intelevia.com</p>
              <p className="text-emerald-200 text-sm">{copy.cta.response}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
